<template>
  <main
    class="wrapper-content pr-0"
    :class="{ 'd-flex flex-column justify-content-between pt-4': isError }"
  >
    <!-- Loading -->
    <LmComponentLoading v-if="isLoading" fixed />
    <b-row v-if="!isError">
      <b-col>
        <portal to="documentSubTitle">
          <div class="col-12 px-0">
            <h5>Tipo de documento: {{ documentTypeName }}</h5>
          </div>
        </portal>
        <portal to="documentName">
          <!-- Input del nombre -->
          <LmTextInput
            class="col-12 px-0"
            v-model="$v.form.documentName.$model"
            :v="validation('form.documentName')"
            :label="$t('name')"
            :placeholder="$t('name')"
            :disabled="!canBeEditedByMe && isOnEditMode"
          />
        </portal>
        <!-- Formbuilder Server Components -->
        <LmFormBuilder
          class="px-0"
          v-if="formbuilderId && (canBeEditedByMe || !isOnEditMode)"
          ref="lmFormbuilder"
          :endpointUriwhereGetComponents="formbuilderPath"
          :endpointUriWhereGetValuesToLoadInEditMode="dataPathOnEditMode"
          :contentTemplateData="form"
          @formObjectToSendToServerChange="updateForm($event)"
        />

        <!-- Vista en modo PDF -->
        <div class="wrapper-content px-0">
          <div v-if="!canBeEditedByMe && documentInPdf" class="row w-100 mx-0">
            <div class="col-sm-12 col-md-12 w-100 my-4">
              <h2 class="m-0">{{form.documentName}}</h2>
              <a-steps :current="form.documentSteps.position" size="small" direction="horizontal">
                <a-step
                  class="mt-4"
                  v-for="(item, index) in form.documentSteps.items"
                  :key="`step-${index}`"
                  :title="item.title" :description="item.description"
                />
                </a-steps>
            </div>
            <iframe id="pdfViewer" :src="documentInPdf.src" width="100%" :height="pdfViewerHeight"/>
          </div>

          <!-- Doc Settings -->
          <div v-if="canBeEditedByMe || !isOnEditMode">
            <h3>Configuraciones del documento</h3>

            <b-card no-body class="border my-4">
              <b-tabs card>
                <b-tab title="Participantes" key="signers" v-if="canBeEditedByMe || !isOnEditMode">
                  <LmParticipants
                    :label="$t('users')"
                    placeholder="Seleccione los usuarios que van a participar de este documento"
                    v-model="form.participants"
                    :canAddReviewers="canAddReviewers"
                    :canAddExternalParticipants="canAddExternalParticipants"
                  />
                </b-tab>
                <b-tab title="Destinatarios" key="recipients" v-if="(canBeEditedByMe || !isOnEditMode) && isComunicable">
                  <LmRecipients
                    v-model="form.recipients"
                    :canAddExternalRecipients="canAddExternalRecipients"
                  />
                </b-tab>
                <b-tab title="Compartir con" key="sharewith" v-if="canBeEditedByMe || !isOnEditMode">
                  <LmShareWith
                    v-model="form.shareWith"
                  />
                </b-tab>
                <b-tab title="Visualizadores" key="viewers" v-if="!isPublic && (canBeEditedByMe || !isOnEditMode)">
                  <LmViewers
                    v-model="form.viewers"
                  />
                </b-tab>
              </b-tabs>
            </b-card>
          </div>
        </div>
      </b-col>
      <!-- Toolbar -->
      <LmToolbar
        ref="toolbar"
        :toolbarHeight="toolbarHeight"
        :isOnEditMode="isOnEditMode"
        :items="toolbarItems"
        :config="toolbarConfig"
        @onContentTemplateChange="onContentTemplateChange"
      />
    </b-row>

    <LmSvgIcon
      v-if="isError"
      icon="LmAccessDeniedIcon"
      wrapperClass="my-4 d-flex align-items-center flex-column"
      text="No pudimos cargar el documento solicitado."
      width="12em"
    />

    <!-- Action Buttons -->
    <b-row class="mt-5">
      <LmButton
        :value="goBackOrCancelButton ? 'Volver' : $t('cancel')"
        class="col-auto"
        type="button"
        buttonClass="btn-secondary"
        @click="onCancel()"
      />
      <LmButton
        v-if="(isOnEditMode && canBeEditedByMe ) || !isOnEditMode"
        :value="$t('save_as_draft')"
        class="col-auto"
        type="button"
        buttonClass="btn-primary"
        @click="onSubmit()"
      />
      <LmButton
        v-if="!isOnEditMode"
        :value="$t('start_document')"
        class="col-auto"
        type="button"
        buttonClass="btn-success"
        @click="onSendToStart('create')"
      />
      <LmButton
        v-if="(isOnEditMode && canBeEditedByMe)"
        :value="$t('start_document')"
        class="col-auto"
        type="button"
        buttonClass="btn-success"
        @click="onSendToStart('update')"
      />
      <LmButton
        v-if="isOnEditMode && canBeSignedByMe"
        :value="$t('sign')"
        class="col-auto"
        type="button"
        buttonClass="btn-success"
        @click="onSign()"
      />
      <LmButton
        v-if="isOnEditMode && canBeReviewedByMe"
        :value="$t('approve')"
        class="col-auto"
        type="button"
        buttonClass="btn-success"
        requireConfirmation
        @click="onReview()"
      />
      <LmButton
        v-if="isOnEditMode && canBeRejectedByMe"
        :value="$t('reject')"
        class="col-auto"
        type="button"
        buttonClass="btn-danger"
        @click="onReject()"
        requireConfirmation
        confirmationTitle="¿Estas seguro de rechazar el documento?"
        confirmationMessage="El documento se pondrá en edición y se notificará al creador."
      />
      <LmButton
        v-if="canBeEditedByMe && isOnEditMode"
        :value="$t('delete')"
        class="col-auto"
        type="button"
        action="delete"
        buttonClass="btn-danger"
        @click="onDelete()"
      />
      <LmButton
        v-if="canReSendEmailToExternalSigner"
        :value="`Reenviar Email a ${lumbreNextSigner.firstname + ' ' + lumbreNextSigner.lastname}`"
        class="col-auto"
        type="button"
        buttonClass="btn-info"
        @click="onResendTokenToSigner()"
      />
    </b-row>
    <lmConfirmationModal
      ref="lmConfirmationModalFirmarToken" title="Aviso"
      message="Va a iniciar el proceso de firma del documento.  ¿Está seguro?"
    />
    <lmConfirmationModal ref="lmConfirmationModalLicenses" title="Aviso" />
    <LmSendOTP
      ref="lmValidationModalFirmarToken" title="Aviso"
      message="Un código fue enviado a su mail, por favor ingreselo a continuación." @ok="setToken"
    />
  </main>
</template>

<script>
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmFormBuilder from '@/components/LmFormBuilder'
import LmParticipants from '@/components/form/elements/LmParticipants'
import LmRecipients from '@/components/form/elements/LmRecipients'
import LmShareWith from '@/components/form/elements/LmShareWith'
import LmViewers from '@/components/form/elements/LmViewers'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import commonsApi from '@/api/commons'
import axios from 'axios'
import lmToasts from '@/mixins/lmToasts.js'
import { EventBus } from '@/event-bus.js'
import lmConfirmationModal from '@/components/modals/LmConfirmationModal'
import LmSendOTP from '@/components/modals/LmSendOTP'

import LmToolbar from '@/components/lmToolBar'

import _ from 'lodash'

import { mapMutations } from 'vuex'

import {
  required
} from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  components: {
    LmToolbar,
    LmFormBuilder,
    LmButton,
    LmParticipants,
    LmRecipients,
    LmShareWith,
    LmTreeSelect,
    LmTextInput,
    lmConfirmationModal,
    LmSendOTP,
    LmViewers
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    recipientsConfig: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      formbuilderId: '',
      listbuilderId: '',
      documentTypeId: '',
      documentTypeName: '',
      documentTypeComments: '',
      documentTypeHidecCommentsOnFinished: false,
      isComunicable: false,
      isPublish: false,
      isPublic: false,
      signatureType: null,
      isOnLicense: false,
      canBeEditedByMe: false,
      canBeSignedByMe: false,
      canBeRejectedByMe: false,
      canBeSendToSignByMe: false,
      canBeReviewedByMe: false,
      canReSendEmailToExternalSigner: false,
      canAddReviewers: true,
      canAddExternalParticipants: true,
      canAddExternalRecipients: true,
      documentInPdf: undefined,
      lumbreNextSigner: null,
      lumbreStatusName: null,
      initialForm: '',
      form: {
        documentName: '',
        participants: [],
        documentSteps: {
          position: 0,
          items: []
        },
        recipients: {
          users: [],
          areas: [],
          subareas: [],
          groups: [],
          emails: []
        },
        viewers: {
          users: [],
          areas: [],
          subareas: []
        },
        shareWith: []
      },
      isError: false,
      isLoading: false,
      otpToken: '',
      documentHasAnyContent: false,
      errorOcurredOnSign: false,
      processDocument: false
    }
  },
  validations: {
    form: {
      documentName: {
        required
      }
    }
  },
  watch: {
    form: {
      deep: true,
      handler (val) {
        this.documentHasAnyContent = true
      }
    }
  },
  computed: {
    // ...mapGetters('processes', ['createdByEditableProcess', 'editableProcessPath']),
    goBackOrCancelButton () {
      return !!this.$route?.params?.pathToRedirectOnCancel
    },
    toolbarItems () {
      let items = []
      if (this.isOnEditMode) {
        if (process.env?.VUE_APP_DISABLE_TAGS !== 'yes') {
          items.push('tags')
        }
        items = items.concat([
          'documentMovements',
          'auditMovements',
          'documentPreview',
          null
        ])
        if (!this.hideComments) {
          items.unshift('comments')
        }
        if (this.canBeEditedByMe) {
          items.unshift('contentTemplates')
          if (!this.isPublic) {
            items.push('documentViewers')
          }
        } else {
          items.push('documentModel')
          if (this.isComunicable) {
            items.push('documentRecipients')
          }
        }
      } else {
        items = items.concat([
          'contentTemplates',
          'documentPreview'
        ])
      }
      return items
    },
    toolbarConfig () {
      return {
        comments: {
          linkType: 'documents',
          disabled: this.disableComments
        },
        documentPreview: {
          documentTypeId: this.documentTypeId,
          dataToRender: this.form
        },
        documentModel: {
          formbuilderPath: this.formbuilderPath,
          dataPathOnEditMode: this.dataPathOnEditMode
        },
        documentRecipients: {
          recipients: this.form.recipients
        },
        documentViewers: {
          viewers: this.form.viewers
        },
        contentTemplates: {
          path: `/contenttemplate/${this.documentTypeId}/documenttypes/all`,
          template: this.form,
          reference: this.documentTypeId
        },
        auditMovements: {
          serviceName: 'documents'
        },
        tags: {
          serviceName: 'documents'
        }
      }
    },
    contentTemplateOptionsFiltered () {
      return this.contentTemplateOptions.filter(opt => {
        if (this.contentTemplateFilter === '') {
          return true
        } else {
          return opt.name.toLowerCase().search(this.contentTemplateFilter.toLowerCase()) !== -1
        }
      })
    },
    documentRecipients () {
      let recipients = []
      for (const key in this.form.recipients) {
        recipients = recipients.concat(this.form.recipients[key].map(r => {
          return {
            ...r,
            icon: this.setIcon(key)
          }
        }))
      }
      return recipients.length ? recipients : null
    },
    documentViewers () {
      let viewers = []
      for (const key in this.form.viewers) {
        viewers = viewers.concat(this.form.viewers[key].map(r => {
          return {
            ...r,
            icon: this.setIcon(key)
          }
        }))
      }
      return viewers.length ? viewers : null
    },
    // Calculo el ancho maximo que tiene el toolbar
    toolbarHeight () {
      return `max-height: calc(${this.pdfViewerHeight} + 39px + 3rem ${this.form?.documentSteps?.items?.length ? '+ 69px' : ''})`
    },
    formbuilderPath () {
      return `/formbuilder/${this.formbuilderId}`
    },
    dataPathOnEditMode () {
      if (this.isOnEditMode) {
        return `/documents/${this.$route?.params?.id}`
      } else {
        return ''
      }
    },
    stepsDirection: {
      get () {
        return this.form.participants.length > 6 ? 'vertical' : 'horizontal'
      },
      set () {}
    },
    pdfViewerColumns: {
      get () {
        return this.stepsDirection === 'vertical' ? 'col-sm-12 col-md-10' : 'col-12'
      },
      set () {}
    },
    pdfViewerHeight: {
      get () {
        return this.stepsDirection === 'vertical' ? '100%' : '700px'
      },
      set () {}
    },
    disableComments () {
      if (this.documentTypeComments === 'always') {
        return false
      }
      if (this.documentTypeComments === 'in_progress') {
        if (this.lumbreStatusName !== 'Finalizado') {
          return false
        }
        return true
      }
      return false
    },
    hideComments () {
      if (this.documentTypeComments === 'in_progress' && this.lumbreStatusName === 'Finalizado' && this.documentTypeHidecCommentsOnFinished) {
        return true
      }
      if (this.documentTypeComments === 'never') {
        return true
      }
      return false
    },
    enableLumileAppSigner () {
      return process.env?.VUE_APP_ENABLE_LUMILE_APP_SIGNER === 'yes'
    }
  },
  methods: {
    ...mapMutations('processes', ['setDocument']),
    validation (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    onContentTemplateChange (event) {
      for (const key in this.form) {
        if (event.hasOwnProperty(key)) {
          this.form[key] = event[key]
        }
      }
    },
    validateParticipants () {
      if (this.form?.participants?.at(-1)?.action === 'R') {
        this.lmToastDangerMessage('Lo sentimos pero el último participante debe ser un firmante.', 'Error')
        throw new Error('El último participante debe ser un firmante.')
      }
    },
    async participantsLicencesNotification () {
      const participants = this.form.participants
        .filter(el => !el?.userType || el?.userType !== 'external')
        .map(el => el.id)
      const res = await commonsApi.axiosPostWrapper('/documents/validate-participants', {
        participants
      })
      if (res.data.length) {
        let content = ''
        res.data.forEach(el => {
          let substitute = ''
          if (el.substitute) {
            substitute = `Esta siendo reemplazado por ${el.substitute.name}`
          }
          const user = this.form.participants.find(participant => participant.id === el.id)
          content += `<p>${user.name} tiene licencia hasta el ${el.end}. ${substitute}</p>`
        })
        this.$refs.lmConfirmationModalLicenses.setMessageProps(content)
        await this.$refs.lmConfirmationModalLicenses.lmShow()
        console.log('confirmo')
      }
    },
    async onSubmit () {
      try {
        this.isLoading = true

        // Validaciones
        this.validateParticipants()
        await this.validateForm(this.$v)
        await this.$refs.lmFormbuilder.validate()

        const dataToSendToServer = {
          ...this.form,
          documentTypeId: this.documentTypeId
        }
        let res
        if (!this.isOnEditMode) {
          // nuevo
          res = await commonsApi.axiosPostWrapper('/documents/', dataToSendToServer)
          this.lmToastSuccessMessage(`Se creó ${res?.data?.documentName || ' el documento'} correctamente.`, 'Se guardó exitosamente')
        } else {
          // editar
          res = await commonsApi.axiosPutWrapper(`/documents/update/${this.$route?.params?.id}`, dataToSendToServer)
          this.lmToastSuccessMessage(`Se editó ${res?.data?.documentName || ' el documento'} correctamente.`, 'Se guardó exitosamente')
        }

        // Si este documento se crea como parte de un proceso (trámite)
        if (this.processDocument) {
          this.setDocument({
            id: res.data._id,
            name: res.data.documentName
          })
          this.$router.push(this.processDocument)
        } else {
          setTimeout(() => {
            this.$router.push('/inbox')
          }, 650)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onResendTokenToSigner () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper('/documents/resendEmail-to-external', {
          id: this.$route.params.id
        })
        this.lmToastSuccessMessage('Se reenvió la notificación correctamente', 'Enviado con éxito')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSendToStart (mode) {
      // Validación
      try {
        this.validateParticipants()
        await this.validateForm(this.$v)
        await this.$refs.lmFormbuilder.validate()
        await this.participantsLicencesNotification()
      } catch (error) {
        return false
      }
      try {
        let redirectAfterFinished = true
        this.isLoading = true
        const targetEndpoint = mode === 'create' ? '/documents/createandstart' : '/documents/start'
        const res = await commonsApi.axiosPostWrapper(targetEndpoint, {
          id: this.$route.params.id,
          documentTypeId: this.documentTypeId,
          ...this.form
        })
        if (process?.env?.VUE_APP_LUMBRE_LICENSES_ENABLED === 'yes') {
          if (res.data.action === 'inmediateSign' && !this.isOnLicense) {
            await this.onSign(res.data.docId)
            redirectAfterFinished = !this.errorOcurredOnSign
          }
          if (res.data.action === 'inmediateReview' && !this.isOnLicense) {
            await this.onReview(res.data.docId)
          }
        } else {
          if (res.data.action === 'inmediateSign') {
            await this.onSign(res.data.docId)
            redirectAfterFinished = !this.errorOcurredOnSign
          }
          if (res.data.action === 'inmediateReview') {
            await this.onReview(res.data.docId)
          }
        }
        this.lmToastSuccessMessage(`Se inició ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento Iniciado.')

        // Si este documento se crea como parte de un proceso (trámite)
        if (this.processDocument) {
          this.setDocument({
            id: res.data._id,
            name: res.data.documentName
          })
          this.$router.push(this.processDocument)
        } else if (redirectAfterFinished) {
          setTimeout(() => {
            this.$router.push('/inbox')
          }, 650)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSign (id) {
      try {
        const documentId = this.$route.params?.id || id
        await this.$refs.lmConfirmationModalFirmarToken.lmShow()
        this.errorOcurredOnSign = false
        if (this?.signatureType === 'electronic') {
          this.isLoading = true
          await commonsApi.axiosPostWrapper('/documents/generateotp', {
            id: documentId
          })
          this.isLoading = false
          this.$refs.lmValidationModalFirmarToken.setIsCommentProps(true, true, 'Codigo', 'token')
          await this.$refs.lmValidationModalFirmarToken.lmShow()
          await commonsApi.axiosPostWrapper(`/documents/sign`, {
            id: documentId,
            token: this.otpToken
          })
          this.lmToastSuccessMessage(`Se firmó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento firmado.')
        } else if (this?.signatureType === 'electronic_without_otp') {
          this.isLoading = true
          await commonsApi.axiosPostWrapper(`/documents/sign`, {
            id: documentId,
            token: this.otpToken
          })
          this.isLoading = false
          this.lmToastSuccessMessage(`Se firmó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento firmado.')
        } else if (this?.signatureType === 'digital') {
          if (this.enableLumileAppSigner) {
            this.isLoading = true
            try {
              await this.checkLumbreDocsSigner()
            } catch (error) {
              this.errorOcurredOnSign = true
            }
            const r = await commonsApi.axiosGetWrapper(`/documents/generatejnlpV2/${documentId}`)
            await commonsApi.axiosPostWrapper('http://localhost:8081/initSR', {
              url: r.data.targetURL
            })
          } else {
            const tempLink = document.createElement('a')
            tempLink.href = `${process.env.VUE_APP_API_URL}documents/generatejnlp/${documentId}`
            tempLink.setAttribute('download', `documento_afirmar.jnlp`)
            tempLink.click()
            this.lmToastSuccessMessage(`Para continuar debe finalizar el proceso de firma desde la aplicacion.`, 'Enviado al Firmador.')
          }
        }
        if (!this.processDocument && !this.errorOcurredOnSign) {
          setTimeout(() => {
            this.$router.push('/inbox')
          }, 650)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onReview (id) {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`/documents/review`, {
          id: this.$route.params.id || id
        })
        this.lmToastSuccessMessage(`Se revisó el documento "${this.form?.documentName || ' el documento'}" correctamente.`, 'Documento revisado.')
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onReject () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`/documents/reject`, {
          id: this.$route.params.id
        })
        this.lmToastSuccessMessage(`Se rechazó el documento "${this.form?.documentName || ' el documento'}" correctamente.`, 'Documento rechazado.')
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onDelete () {
      try {
        this.isLoading = true
        await commonsApi.axiosPutWrapper(`/documents/delete/${this.$route.params.id}`)
        this.lmToastSuccessMessage(`Se eliminó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento eliminado.')
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onCancel () {
      if (this.goBackOrCancelButton) {
        this.$router.push(this.$route.params.pathToRedirectOnCancel)
      } else {
        this.$router.push('/inbox')
      }
    },
    updateForm (event) {
      this.form = Object.assign({}, this.form, event)
    },
    validateForm (validations) {
      return new Promise((resolve, reject) => {
        let error = false
        validations.$touch()
        error = validations.$error
        if (error) {
          for (const key in this.form) {
            if (validations.form.hasOwnProperty(key)) {
              if (validations.form[key].$error) {
                const selector = this.$el.querySelector(`#${key}`)
                selector.scrollIntoView({ behavior: 'smooth' })
                break
              }
            }
          }
          this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error de validación')
          reject(new Error('validation error'))
        } else {
          resolve(true)
        }
      })
    },
    async loadContentOnEditMode () {
      if (this.isOnEditMode) {
        try {
          this.isError = false
          this.isLoading = true
          const document = await commonsApi.axiosGetWrapper(`/documents/${this.$route.params.id}`)
          this.form.documentName = document.data.documentName
          this.form.participants = document.data?.participants || []
          this.form.recipients = document.data?.recipients || {}
          if (document.data.documentSteps) {
            this.form.documentSteps = document.data.documentSteps
          }
          this.form.shareWith = document.data?.shareWith || []
          this.form.viewers = document.data?.viewers || {}
          this.canBeEditedByMe = document.data?.canBeEditedByMe || false
          this.canBeSignedByMe = document.data?.canBeSignedByMe || false
          this.canBeRejectedByMe = document.data?.canBeRejectedByMe || false
          this.canBeSendToSignByMe = document.data?.canBeSendToSignByMe || false
          this.documentInPdf = document.data?.documentInPdf || undefined
          this.lumbreNextSigner = document.data?.lumbreNextSigner || null
          this.canReSendEmailToExternalSigner = document.data?.canReSendEmailToExternalSigner || false
          this.canBeReviewedByMe = document.data?.canBeReviewedByMe || false
          this.lumbreStatusName = document.data?.lumbreStatusName || null
        } catch (error) {
          console.log(error)
          this.isError = true
        } finally {
          this.isLoading = false
        }
      }
    },
    setToken (data) {
      this.otpToken = data.comment
    },
    async checkLumbreDocsSigner () {
      try {
        const api = axios.create()
        const localSignerPromise = api('http://localhost:8081/version')
        const signerLastVersionPromise = api('/versions/lumbre-signer')
        const [localSigner, signerLastVersion] = await Promise.all([localSignerPromise, signerLastVersionPromise])
        let error
        if (parseFloat(localSigner.data.version) < parseFloat(signerLastVersion.data)) {
          error = {
            type: 'update_required',
            message: 'LumbreDocs Signer necesita actualizarse.'
          }
        }
        if (error) throw error
      } catch (error) {
        if (error?.type === 'update_required') {
          this.lmToastDangerMessage(
            error.message,
            'Error'
          )
        } else {
          if (process.env?.VUE_APP_ENABLE_REAMAZE === 'yes') {
            window.Reamaze.popup('/kb/info-tecnica/como-descargar-el-firmador')
            this.lmToastDangerMessage(
              'Para poder hacer una firma digital necesita tener instalado LumbreDocs Signer, siga los pasos que se muestran en la ayuda.',
              'Error'
            )
          }
        }
        throw error
      }
    }
  },
  async created () {
    try {
      this.initialForm = JSON.stringify(this.form)

      if (process?.env?.VUE_APP_LUMBRE_LICENSES_ENABLED === 'yes') {
        const isOnLicense = await commonsApi.axiosGetWrapper('/licenses/imonlicense')
        this.isOnLicense = isOnLicense.data
      } else {
        this.isOnLicense = false
      }

      const documentType = await commonsApi.axiosGetWrapper(`/documenttypes/${this.$route.params.docTypeId}`)
      this.formbuilderId = documentType.data.formbuilderId
      this.listbuilderId = documentType.data.listbuilderId
      this.documentTypeId = documentType.data._id
      this.documentTypeName = documentType.data.typeName
      this.isComunicable = documentType.data.typeComunicable === 'comunicable'
      this.isPublish = documentType.data.publish
      this.isPublic = documentType.data.typeVisibility === 'public'
      this.signatureType = documentType.data.typeSignature
      this.documentTypeComments = documentType.data?.typeComments || 'always'
      this.documentTypeHidecCommentsOnFinished = documentType.data?.typeHideCommentsOnFinished || false
      this.canAddReviewers = documentType.data?.typeAllowReviewers
      this.canAddExternalParticipants = documentType.data?.typeAllowExternalParticipants
      this.canAddExternalRecipients = documentType.data?.typeAllowExternalRecipients

      if (!this.isOnEditMode && !this.isPublish) {
        EventBus.$emit('lmShowGlobalToast', {
          toastMessage: 'Debes publicar el Tipo de Documento para poder añadirle Documentos.',
          toastAttributes: {
            title: 'Error',
            toaster: 'b-toaster-bottom-right',
            variant: 'danger',
            solid: true
          }
        })
        this.$router.push('/inbox')
      }
      await this.loadContentOnEditMode()
    } catch (error) {
      console.log(error)
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (from.name === 'process-nuevo' || from.name === 'process-editar') {
        vm.processDocument = from.fullPath
      }
    })
  }
}
</script>

<style lang="sass" scoped>
  #pdfViewer
    min-height: 500px
</style>
